import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Users, Building2, Mail, Phone, Eye, Hash } from 'lucide-react';
import { CustomerFormDialog } from '@/components/Customers/CustomerFormDialog';
import type { Customer } from '@/types/legacy';

export default function Customers() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const fetchCustomers = async () => {
    try {
      const { data, error } = await api
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los clientes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);
  useDataRefresh(fetchCustomers, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredCustomers = customers.filter(customer =>
    customer.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (customer: any) => {
    setDeleteDialog({ open: true, customer });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.customer) return;
    const { error } = await api.from('customers').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', deleteDialog.customer.id);
    if (error) { toast({ title: 'Error', description: 'No se pudo eliminar: ' + error.message, variant: 'destructive' }); return; }
    toast({ title: 'Eliminado', description: 'Marcado como inactivo (historico conservado).' });
    fetchCustomers();
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Gestión de Clientes
            </h1>
            <p className="text-muted-foreground mt-2">
              Administración de clientes y contactos
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clientes</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {customers.length}
                </p>
              </div>
              <Users className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-3xl font-bold text-success">
                  {customers.filter(c => c.is_active).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Empresas</p>
                <p className="text-3xl font-bold text-primary">
                  {customers.filter(c => c.company_name).length}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-primary/50" />
            </div>
          </Card>
        </div>

        {/* Customers Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron clientes' : 'No hay clientes registrados'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCustomers.map((customer) => (
              <Card key={customer.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="gap-1 text-xs font-mono bg-primary/10">
                        C-{String((customer as any).customer_number || 0).padStart(3, '0')}
                      </Badge>
                      {(customer as any).codificacion && (
                        <Badge variant="secondary" className="text-xs">
                          {(customer as any).codificacion}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg">
                      {customer.company_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Sin nombre'}
                    </h3>
                    {customer.company_name && (customer.first_name || customer.last_name) && (
                      <p className="text-sm text-muted-foreground">
                        {customer.first_name} {customer.last_name}
                      </p>
                    )}
                  </div>
                  <Badge variant={customer.is_active ? 'default' : 'secondary'}>
                    {customer.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm">
                  {customer.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {customer.address && (
                    <p className="text-muted-foreground line-clamp-2 mt-2">
                      {customer.address}
                    </p>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-border/50 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Detalles
                  </Button>
                  {canManage && (
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(customer)}
                    >
                      Editar
                    </Button>
                  )}
                  {canManage && customer.is_active && (
                    <Button variant="outline" size="sm" className="flex-1 text-destructive" onClick={() => handleDelete(customer)}>
                      Eliminar
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <CustomerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={selectedCustomer}
      />
    </DashboardLayout>
  );
}
